import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Spinner,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { AppContext } from '../../../App';
import { getProjectSelectList } from '../../../clients/project_client';
import { getUserSelectList } from '../../../clients/user_client';
import DropdownSelect from '../../Generic/DropdownSelect';
import { emailRegex } from '../../../config_and_helpers/config';
import { findUserOption } from '../../../config_and_helpers/userDropdownOptions';
import { logInfo } from '../../../config_and_helpers/logger';

const LOG_CONTEXT = 'ImportApplyPanel';

/** @typedef {'create'|'existing'|'none'} ProjectMode */

/**
 * Step C/D: project choice, toggles, contact, apply trigger and success display.
 *
 * @param {Object} props
 * @param {Object} props.previewData
 * @param {Set<number>} props.selectedRowIndexes
 * @param {string} props.suggestedProjectName
 * @param {boolean} props.isApplying
 * @param {string[]} props.applyErrors
 * @param {Function} props.onApply - (payload) => void
 * @param {Object|null} props.applyResult - Success response data
 * @param {Function} props.onReset
 */
const ImportApplyPanel = ({
  previewData,
  selectedRowIndexes,
  suggestedProjectName,
  isApplying,
  applyErrors,
  onApply,
  applyResult,
  onReset,
}) => {
  const { token } = useContext(AppContext);
  const matchedCount = previewData?.summary?.matched_count ?? 0;

  const [projectMode, setProjectMode] = useState(
    matchedCount > 0 ? 'create' : 'none'
  );
  const [projectName, setProjectName] = useState(suggestedProjectName || '');
  const [projectDescription, setProjectDescription] = useState('');
  const [projectBudget, setProjectBudget] = useState('');
  const [existingProjectId, setExistingProjectId] = useState('');
  const [projectOptions, setProjectOptions] = useState([]);
  const [linkMatchedToProject, setLinkMatchedToProject] = useState(
    matchedCount > 0
  );
  const [createOrderRequest, setCreateOrderRequest] = useState(false);
  const [contactUserId, setContactUserId] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [orderRequestNotes, setOrderRequestNotes] = useState('From list import');
  const [projectProductNotes, setProjectProductNotes] = useState('');
  const [userSelectList, setUserSelectList] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    if (token) {
      getProjectSelectList(token, setProjectOptions);
    }
  }, [token]);

  useEffect(() => {
    if (!createOrderRequest || !token) return;
    setIsLoadingUsers(true);
    getUserSelectList(token, setUserSelectList).finally(() => {
      setIsLoadingUsers(false);
    });
  }, [createOrderRequest, token]);

  useEffect(() => {
    setProjectName(suggestedProjectName || '');
  }, [suggestedProjectName]);

  useEffect(() => {
    if (projectMode === 'none') {
      setLinkMatchedToProject(false);
    } else {
      setLinkMatchedToProject(true);
    }
  }, [projectMode]);

  const selectedRows = (previewData?.rows ?? []).filter(r =>
    selectedRowIndexes.has(r.row_index)
  );

  const validate = () => {
    const errors = [];
    if (selectedRows.length === 0) {
      errors.push('Select at least one in-inventory row to continue.');
    }
    if (!linkMatchedToProject && !createOrderRequest) {
      errors.push(
        'Enable “Add matched items to project” and/or “Create order request”.'
      );
    }
    if (linkMatchedToProject) {
      if (projectMode === 'create' && !projectName.trim()) {
        errors.push('Enter a name for the new project.');
      }
      if (projectMode === 'existing' && !existingProjectId) {
        errors.push('Select an existing project.');
      }
    }
    if (createOrderRequest) {
      const userId = Number(contactUserId);
      const hasUser = Number.isInteger(userId) && userId > 0;
      const hasManual =
        contactName.trim() && emailRegex.test(contactEmail.trim());
      if (!hasUser && !hasManual) {
        errors.push(
          'For an order request, select a related user or enter contact name and email.'
        );
      }
    }
    return errors;
  };

  const handleSubmit = e => {
    e.preventDefault();
    const errors = validate();
    setValidationErrors(errors);
    if (errors.length) {
      logInfo(LOG_CONTEXT, 'apply validation failed', { errors });
      return;
    }

    const payload = {
      rows: selectedRows,
      project_id: null,
      link_matched_to_project: linkMatchedToProject,
      create_order_request: createOrderRequest,
      order_request_notes: orderRequestNotes.trim(),
      project_product_notes: projectProductNotes.trim(),
      contact_name: '',
      contact_email: '',
    };

    if (linkMatchedToProject) {
      if (projectMode === 'create') {
        payload.create_project = {
          name: projectName.trim(),
          description: projectDescription.trim(),
          budget:
            projectBudget.trim() === ''
              ? null
              : Number(projectBudget) || null,
        };
      } else if (projectMode === 'existing') {
        payload.project_id = Number(existingProjectId);
      }
    }

    if (createOrderRequest) {
      const userId = Number(contactUserId);
      if (Number.isInteger(userId) && userId > 0) {
        payload.contact_user_id = userId;
      } else {
        payload.contact_name = contactName.trim();
        payload.contact_email = contactEmail.trim();
      }
    }

    logInfo(LOG_CONTEXT, 'submit apply', {
      rowCount: payload.rows.length,
      projectMode,
      linkMatchedToProject,
      createOrderRequest,
    });
    onApply(payload);
  };

  if (applyResult) {
    const project = applyResult.project;
    const orderRequest = applyResult.order_request;
    const skipped = applyResult.skipped_rows ?? [];

    return (
      <div className="mb-4">
        <Alert variant="success">
          <h5 className="alert-heading">Import completed</h5>
          {project ? (
            <p className="mb-1">
              Project:{' '}
              <Link to={`/projects/${project.id}`}>{project.name}</Link>
            </p>
          ) : null}
          {applyResult.project_products_linked != null ? (
            <p className="mb-1">
              Project products linked: {applyResult.project_products_linked}
            </p>
          ) : null}
          {orderRequest?.request_ref ? (
            <p className="mb-1">
              Order request:{' '}
              <Link to="/order-requests">
                {orderRequest.request_ref}
              </Link>
            </p>
          ) : null}
          {skipped.length > 0 ? (
            <details className="mt-2">
              <summary>
                {skipped.length} row(s) skipped during apply
              </summary>
              <ul className="mb-0 mt-2 small">
                {skipped.map((s, i) => (
                  <li key={i}>
                    Row {s.row_index}: {s.reason}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </Alert>
        <Button variant="primary" className="glass-primary-btn" onClick={onReset}>
          Import another file
        </Button>
      </div>
    );
  }

  const displayErrors = [...validationErrors, ...(applyErrors || [])];

  return (
    <div className="mb-4">
      <h5 className="mb-3">Project and actions</h5>
      <p className="text-muted small">
        {selectedRows.length} inventory row(s) selected for import.
      </p>

      {displayErrors.length > 0 ? (
        <Alert variant="danger">
          {displayErrors.map((msg, i) => (
            <div key={i}>{msg}</div>
          ))}
        </Alert>
      ) : null}

      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Project</Form.Label>
          <Form.Check
            type="radio"
            id="project-mode-create"
            name="projectMode"
            label="Create new project from this list"
            checked={projectMode === 'create'}
            onChange={() => setProjectMode('create')}
            disabled={matchedCount === 0}
          />
          <Form.Check
            type="radio"
            id="project-mode-existing"
            name="projectMode"
            label="Link to existing project"
            checked={projectMode === 'existing'}
            onChange={() => setProjectMode('existing')}
            disabled={matchedCount === 0}
          />
          <Form.Check
            type="radio"
            id="project-mode-none"
            name="projectMode"
            label="Do not link to a project"
            checked={projectMode === 'none'}
            onChange={() => setProjectMode('none')}
          />
        </Form.Group>

        {projectMode === 'create' ? (
          <>
            <Form.Group className="mb-2">
              <Form.Label>Project name</Form.Label>
              <Form.Control
                value={projectName}
                onChange={e => setProjectName(e.target.value)}
                required={linkMatchedToProject}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={projectDescription}
                onChange={e => setProjectDescription(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Budget (optional)</Form.Label>
              <Form.Control
                type="number"
                value={projectBudget}
                onChange={e => setProjectBudget(e.target.value)}
              />
            </Form.Group>
          </>
        ) : null}

        {projectMode === 'existing' ? (
          <Form.Group className="mb-3">
            <Form.Label>Existing project</Form.Label>
            <Form.Select
              value={existingProjectId}
              onChange={e => setExistingProjectId(e.target.value)}
            >
              <option value="">Select project…</option>
              {projectOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        ) : null}

        <Form.Check
          type="checkbox"
          id="link-matched"
          className="mb-2"
          label="Add matched items to project"
          checked={linkMatchedToProject}
          disabled={projectMode === 'none'}
          onChange={e => setLinkMatchedToProject(e.target.checked)}
        />

        <Form.Check
          type="checkbox"
          id="create-order-request"
          className="mb-3"
          label="Create order request (procurement queue)"
          checked={createOrderRequest}
          onChange={e => setCreateOrderRequest(e.target.checked)}
        />

        {createOrderRequest ? (
          <>
            <Form.Group className="mb-2">
              <DropdownSelect
                optionsList={userSelectList}
                label="Related user (preferred)"
                selectedValue={findUserOption(userSelectList, contactUserId)}
                setSelectedValue={opt => setContactUserId(opt?.value ?? '')}
                disabled={isLoadingUsers}
                clearable
              />
            </Form.Group>
            <p className="text-muted small">Or enter contact manually:</p>
            <Form.Group className="mb-2">
              <Form.Label>Contact name</Form.Label>
              <Form.Control
                value={contactName}
                onChange={e => setContactName(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Contact email</Form.Label>
              <Form.Control
                type="email"
                value={contactEmail}
                onChange={e => setContactEmail(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Order request notes</Form.Label>
              <Form.Control
                value={orderRequestNotes}
                onChange={e => setOrderRequestNotes(e.target.value)}
              />
            </Form.Group>
          </>
        ) : null}

        {linkMatchedToProject ? (
          <Form.Group className="mb-3">
            <Form.Label>Notes on project products (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={projectProductNotes}
              onChange={e => setProjectProductNotes(e.target.value)}
            />
          </Form.Group>
        ) : null}

        <Button type="submit" variant="primary" className="glass-primary-btn" disabled={isApplying}>
          {isApplying ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Applying…
            </>
          ) : (
            'Confirm import'
          )}
        </Button>
      </Form>
    </div>
  );
};

export default ImportApplyPanel;
